import React, { useCallback, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { classnames } from 'Utils/classnames/classnames.utils';
import { Button } from 'Components/frameworks/button/Button';
import { ChoiceList } from 'Components/frameworks/choice-list/ChoiceList';
import { FacetCategory } from 'Components/widgets/criteria/criteria-facets/search-facets/FacetCategory';

import { Layer } from 'Components/frameworks/layer/Layer';
import { Modal } from 'Components/frameworks/modal/Modal';
import { COLORS } from 'Components/utils/color/colors';

import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  onClearFacets,
  trackFacetChange,
  trackFilterChange,
} from 'Components/widgets/criteria/criteria-facets/search-facets/SearchFacets.utils';
import { t } from 'Src/i18n';
import {
  activeCustomFacetsSelector,
  activeFacetsSelector,
  facetsSelector,
  getSearchFilterList,
} from 'Store/faceting/faceting.selector';
import { toggleFacet } from 'Store/faceting/facetingSearch.action';
import { getDepartureCity } from 'Store/trip-criteria/tripCriteria.selector';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { COUNTRIES_FACET_ID, DESTINATIONS_FACET_ID } from 'Utils/faceting/constants';

import { getFacetTemplateConfig } from 'Components/widgets/criteria/criteria-facets/CriteriaFacets.utils';
import { getChoices } from 'Components/widgets/criteria/criteria-facets/search-facets/FacetList.utils';
import { HeaderLayerContent } from 'Components/widgets/criteria/criteria-facets/search-facets/HeaderLayerContent';
import { NUMBER_OF_TOP_FACETS } from 'Components/widgets/criteria/criteria-facets/search-facets/SearchFacets';
import { InputButton } from 'Components/frameworks/form-inputs/input-button/InputButton';
import { makeWidgetProps } from 'Src/types/widget-helpers';

export const WorldMapFilterList = ({
  clearAllFacets,
  clearFacet,
  closeProductCard,
  isWorldMapFullScreen,
  setDefaultMapValue,
}) => {
  const trackingData = { actionGA: 'worldMap_smartCriterias', categoryGA: 'search' };
  const track = useTracking(trackingData);
  const [criteriaContext] = useContext(CriteriaContext);

  const { isDesktop } = useResponsive();
  const dispatch = useDispatch();
  const activeCustomFacets = useSelector(activeCustomFacetsSelector);
  const activeFacets = useSelector(activeFacetsSelector);
  const departureCity = useSelector(getDepartureCity);
  const facets = useSelector(facetsSelector);
  const searchFilterList = useSelector(getSearchFilterList);
  const [isModalOpened, setIsModalOpened] = useState(false);
  const [idFacet, setFacetLabel] = useState('');
  const [openedFacet, setOpenedFacet] = useState('');

  const onChange = (categoryId, isSelected, value) => {
    const labelGA = trackFacetChange(categoryId, isSelected, value);
    if (labelGA) {
      track({
        labelGA,
      });
    }
    dispatch(
      toggleFacet({
        categoryId,
        isSelected,
        value,
      }),
    );
    setOpenedFacet('');
    closeProductCard();
    setDefaultMapValue();
  };

  const getActiveCount = useCallback(
    (facetId: string) => searchFilterList[facetId]?.activeCount,
    [searchFilterList],
  );

  const handleOpen = (facetId) => {
    openedFacet === facetId ? setOpenedFacet('') : setOpenedFacet(facetId);
    const labelGA = trackFilterChange(facetId);
    track({
      labelGA,
    });
  };

  const getFacetCategoryComponent = (
    <div className="flex flex-col gap-y-20 p-20">
      <FacetCategory
        {...makeWidgetProps<React.ComponentProps<typeof FacetCategory>>({
          templateConfig: getFacetTemplateConfig(criteriaContext, departureCity).find(({ id }) => {
            const topFacetsId = idFacet === COUNTRIES_FACET_ID ? DESTINATIONS_FACET_ID : idFacet;
            return id === topFacetsId;
          }),
          facetsFromSearch: facets,
          onChange,
          activeCustomFacets,
          activeFacets,
          key: idFacet,
          name: idFacet,
        })}
      />
    </div>
  );

  const getFacetLabel = (facetId) => {
    return searchFilterList && t(searchFilterList[facetId]?.title);
  };

  const clearFacetList = () => {
    const openedFacetId = openedFacet === COUNTRIES_FACET_ID ? DESTINATIONS_FACET_ID : openedFacet;
    onClearFacets(
      clearFacet,
      openedFacetId,
      getFacetTemplateConfig(criteriaContext, departureCity).find(({ id }) => id === openedFacetId),
    );
    closeProductCard();
  };

  const ModalTagName = isDesktop ? Modal : Layer;
  const modalProps = isDesktop
    ? {
        closeLabel: t('common.close'),
        onClose: (e) => {
          e.preventDefault();
          setIsModalOpened(false);
        },
        header: (
          <div className="border-darkGrey-lighter border-b p-20 text-20 font-bold leading-1">
            {getFacetLabel(idFacet)}
          </div>
        ),
        hasCloseButton: true,
        size: 'medium',
        children: getFacetCategoryComponent,
      }
    : {
        ariaLabel: t('faceting.filters.filtersTitle'),
        buttonLeft: {
          iconName: ICONS.ARROW_TAIL_LEFT,
          label: t('common.backNavigation'),
          onClick: () => {
            setIsModalOpened(false);
          },
          className: 'flex-1',
        },
        hideCloseButton: true,
        buttonRight: {
          label: t('common.comparator.resetCriteria'),
          onClick: () => clearFacetList(),
          className: 'text-14 underline',
        },
        closeLabel: t('common.close'),
        header: (
          <div className="flex justify-center text-18">
            <HeaderLayerContent
              title={t('faceting.filters.filtersTitle')}
              activeCount={getActiveCount(idFacet)}
            />
          </div>
        ),
        hasAnimateEnter: true,
        bgColor: COLORS.WHITE,
        children: (
          <div className="flex h-full flex-col">
            <div className="scroll-y">{getFacetCategoryComponent}</div>
            <div className="mt-auto border-t p-10">
              <Button
                colorBg={COLORS.SAFFRON}
                colorBorder={COLORS.SAFFRON}
                className="w-full"
                onClick={() => {
                  setIsModalOpened(false);
                }}
                data-testid="apply-filters"
                size="medium"
              >
                {t('common.apply')}
              </Button>
            </div>
          </div>
        ),
      };

  return (
    <div className="absolute inset-x-0 top-0 w-full">
      {Object.keys(searchFilterList).length > 0 && (
        <div className="scrollbar-hidden scroll-x flex gap-x-20 scroll-smooth px-20 pb-20 pt-25 sm:px-30 sm:pt-30">
          {Object.keys(searchFilterList).map((facetId) => {
            const topFacets = searchFilterList[facetId].topFacets ?? [];
            if (topFacets.length === 0) {
              return null;
            }
            return (
              <InputButton
                badgeCount={getActiveCount(facetId)}
                className="whitespace-nowrap bg-white"
                dataTestId={`Criteria-trigger-${facetId}`}
                isActive={openedFacet === facetId}
                isFilled={!!getActiveCount(facetId)}
                key={facetId}
                onClick={() => handleOpen(facetId)}
              >
                {getFacetLabel(facetId)}
              </InputButton>
            );
          })}
          <button
            className="shrink-0 underline"
            onClick={() => {
              clearAllFacets();
              closeProductCard();
            }}
            type="button"
          >
            {t('common.comparator.resetCriteria')}
          </button>
        </div>
      )}
      {openedFacet && (
        <div className="flex max-w-full flex-col gap-y-20 rounded-12 bg-white p-20 sm:mx-30 md:w-300">
          <ChoiceList
            choices={getChoices({
              activeCustomFacets: activeCustomFacets[openedFacet],
              activeFacets: activeFacets[openedFacet],
              facets: searchFilterList[openedFacet].topFacets.slice(0, NUMBER_OF_TOP_FACETS),
              isWorldMapFullScreen,
            })}
            className={classnames('FacetList')}
            name={openedFacet}
            onChange={onChange}
          />
          {searchFilterList[openedFacet].topFacets.length > NUMBER_OF_TOP_FACETS && (
            <button
              type="button"
              onClick={() => {
                track({
                  actionGA: 'worldMap-smartCriterias',
                  labelGA: `${openedFacet}_seeMore`,
                });
                setIsModalOpened(true);
                setFacetLabel(openedFacet);
              }}
              className="text-initial text-14 underline hocus:text-black-active"
            >
              {t('faceting.filters.viewMoreFilters')}
            </button>
          )}
        </div>
      )}
      {isModalOpened && <ModalTagName {...makeWidgetProps<Record<string, unknown>>(modalProps)} />}
    </div>
  );
};
